/**
 * @file Stores for localStorage and sessionStorage
 *   Gracefully handles Storage being unavailable and invalid stored data.
 */

import { JsonFormat, jsonSerializer } from "@redotech/json/format";
import { bijectionOptional } from "@redotech/util/bijection";
import { Store, storeTransform } from "@redotech/util/store";

export interface StorageStore extends Store<string | undefined> {}

/**
 * JSON-encoded store
 */
export function jsonStore<T>(
  store: StorageStore,
  format: JsonFormat<T>,
): Store<T | undefined> {
  return storeTransform(store, bijectionOptional(jsonSerializer(format)));
}

/**
 * localStorage store
 * @see {@link https://developer.mozilla.org/en-US/docs/Web/API/Window/localStorage Window: localStorage property}
 */
export function localStore(key: string): StorageStore {
  try {
    return storageStore(localStorage, key);
  } catch (error) {
    console.error(
      `Failed to create local store for ${key}:\n${(error as Error).stack}`,
    );
    return noOpStore;
  }
}

/**
 * sessionStorage store
 * @see {@link https://developer.mozilla.org/en-US/docs/Web/API/Window/sessionStorage Window: sessionStorage property}
 */
export function sessionStore(key: string): StorageStore {
  try {
    return storageStore(sessionStorage, key);
  } catch (error) {
    console.error(
      `Failed to create session store for ${key}:\n${(error as Error).stack}`,
    );
    return noOpStore;
  }
}

const noOpStore: StorageStore = {
  get() {
    return undefined;
  },
  set() {},
};

/**
 * Returns keys in storage. Unspecified result if storage changes during
 * iteration.
 */
export function* storageKeys(storage: Storage): IterableIterator<string> {
  for (let index = 0; index < storage.length; index++) {
    yield storage.key(index)!;
  }
}

export function storageStore(storage: Storage, key: string): StorageStore {
  return {
    get() {
      try {
        return storage.getItem(key) ?? undefined;
      } catch (error) {
        console.error(`Failed to read ${key}:\n${(error as Error).stack}`);
        return undefined;
      }
    },
    set(value) {
      try {
        if (value === undefined) {
          storage.removeItem(key);
        } else {
          storage.setItem(key, value);
        }
      } catch (error) {
        console.error(`Failed to write ${key}:\n${(error as Error).stack}`);
      }
    },
  };
}
